// DAS-2992: Releasenotes panel — release notes of tickets promoted to Done.
// Rows come from the `release_notes` table (filled by the morning audit's
// sweep). Unchecked notes on top, newest first; search filters on ticket
// number, summary, or note text; the checkbox ticks a note off once the
// owner has verified it.
import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Input, List, Checkbox, Tag, Spin, Empty, Button, message } from 'antd';
import api from '../functions/api';

const ReleaseNotesModal = ({ isVisible, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // DAS-2992(2): reopen a promoted ticket with a comment (Done -> To Do)
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenText, setReopenText] = useState('');
  const [reopening, setReopening] = useState(false);

  const fetchNotes = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await api.get('/release_notes', { params: q ? { search: q } : {} });
      setNotes(res.data.notes || []);
    } catch (e) {
      console.error('release notes fetch failed', e);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isVisible) fetchNotes(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const toggleChecked = async (note) => {
    const checked = !note.checked_at;
    // optimistic flip; refetch restores order + server truth
    setNotes((cur) => cur.map((n) => (n.ID === note.ID
      ? { ...n, checked_at: checked ? new Date().toISOString() : null } : n)));
    try {
      await api.post('/release_notes/check', { id: note.ID, checked });
    } catch (e) {
      console.error('release note check failed', e);
    }
    fetchNotes(search);
  };

  const submitReopen = async () => {
    if (!reopenText.trim()) { message.warning('Write the comment first.'); return; }
    setReopening(true);
    try {
      await api.post('/release_notes/reopen', {
        ticket_key: reopenTarget, comment: reopenText.trim(),
      });
      message.success(`${reopenTarget} reopened (Done \u2192 To Do), comment posted.`);
      setReopenTarget(null);
      setReopenText('');
      fetchNotes(search);
    } catch (e) {
      const detail = e?.response?.data?.error || e.message;
      message.error(`Reopen failed: ${detail}`);
    } finally {
      setReopening(false);
    }
  };

  return (
    <Modal
      title={<span data-testid="release-notes-title">Releasenotes</span>}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width={860}
    >
      <Input.Search
        data-testid="release-notes-search"
        placeholder="Search by text or ticket number…"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onSearch={(v) => fetchNotes(v)}
        style={{ marginBottom: 12 }}
      />
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : notes.length === 0 ? (
        <Empty description="No release notes (yet)" />
      ) : (
        <List
          data-testid="release-notes-list"
          itemLayout="vertical"
          dataSource={notes}
          pagination={{ pageSize: 10, size: 'small' }}
          rowKey="ID"
          renderItem={(note) => (
            <List.Item data-testid={`release-note-${note.ticket_key}`}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <Checkbox
                  checked={!!note.checked_at}
                  onChange={() => toggleChecked(note)}
                  data-testid={`release-note-check-${note.ticket_key}`}
                />
                <a
                  href={`https://monosoftwebsite.atlassian.net/browse/${note.ticket_key}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontWeight: 600 }}
                >
                  {note.ticket_key}
                </a>
                <span style={{ color: '#888' }}>
                  {note.promoted_at ? note.promoted_at.slice(0, 10) : ''}
                </span>
                {!note.checked_at && <Tag color="orange">for you to verify</Tag>}
                <span style={{ fontStyle: 'italic' }}>{note.summary}</span>
                <Button
                  size="small"
                  data-testid={`release-note-reopen-${note.ticket_key}`}
                  onClick={() => { setReopenTarget(note.ticket_key); setReopenText(''); }}
                >
                  Reopen ticket
                </Button>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 6, marginLeft: 24 }}>
                {note.note_md}
              </div>
            </List.Item>
          )}
        />
      )}
      <Modal
        title={`Reopen ${reopenTarget} (Done \u2192 To Do)`}
        open={!!reopenTarget}
        onCancel={() => setReopenTarget(null)}
        onOk={submitReopen}
        okText="Save: comment + reopen"
        confirmLoading={reopening}
      >
        <p>Your comment is posted on the Jira ticket, then the ticket moves back to To Do.</p>
        <Input.TextArea
          rows={4}
          data-testid="release-note-reopen-comment"
          placeholder="Why is this reopened? What should be different?"
          value={reopenText}
          onChange={(e) => setReopenText(e.target.value)}
        />
      </Modal>
    </Modal>
  );
};

export default ReleaseNotesModal;
